import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export type Theme = 'light' | 'dark' | 'system';

interface ThemeStore {
    theme: Theme;
    isDark: boolean;
    setTheme: (theme: Theme) => void;
    toggleTheme: () => void;
    initializeTheme: () => void;
}

// Helper function to get system preference
const getSystemTheme = (): boolean => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
};

// Helper function to calculate if should be dark
const calculateIsDark = (theme: Theme): boolean => {
    if (theme === 'system') {
        return getSystemTheme();
    }
    return theme === 'dark';
};

// Helper function to apply theme to DOM
const applyThemeToDOM = (isDark: boolean) => {
    if (typeof window === 'undefined') return;

    const root = document.documentElement;
    if (isDark) {
        root.classList.add('dark');
    } else {
        root.classList.remove('dark');
    }
};

export const useThemeStore = create<ThemeStore>()(
    persist(
        (set, get) => ({
            theme: 'system',
            isDark: false,

            setTheme: (newTheme: Theme) => {
                const isDark = calculateIsDark(newTheme);
                applyThemeToDOM(isDark);

                set({
                    theme: newTheme,
                    isDark
                });

                // Set up or remove system preference listener
                if (newTheme === 'system') {
                    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
                    const handleChange = () => {
                        const currentState = get();
                        if (currentState.theme === 'system') {
                            const newIsDark = mediaQuery.matches;
                            applyThemeToDOM(newIsDark);
                            set({ isDark: newIsDark });
                        }
                    };

                    // Remove any existing listener first
                    mediaQuery.removeEventListener('change', handleChange);
                    mediaQuery.addEventListener('change', handleChange);
                }
            },

            toggleTheme: () => {
                const currentTheme = get().theme;
                // Simple toggle between light and dark (skips system)
                const newTheme = currentTheme === 'dark' ? 'light' : 'dark';
                get().setTheme(newTheme);
            },

            initializeTheme: () => {
                const currentTheme = get().theme;
                const isDark = calculateIsDark(currentTheme);
                applyThemeToDOM(isDark);

                set({ isDark });

                // Set up system preference listener if in system mode
                if (currentTheme === 'system') {
                    get().setTheme('system'); // This will set up the listener
                }
            },
        }),
        {
            name: 'xtnd-theme-storage',
            storage: createJSONStorage(() => localStorage),
            partialize: (state) => ({ theme: state.theme }), // Only persist theme, not isDark
        }
    )
);

// Custom hook with additional utilities
export const useDarkMode = () => {
    const { theme, isDark, setTheme, toggleTheme, initializeTheme } = useThemeStore();

    return {
        theme,
        isDark,
        setTheme,
        toggleTheme,
        initializeTheme,
        isLight: !isDark,
        isSystem: theme === 'system',
    };
}; 